import { ProductRebillingService } from "../../services";

export interface IProductRebilling {
  title: string;
  description: string;
  type: string;
  rebillingEnabled: boolean;
  helpArticle?: string;
  markup?: number;
  price: number;
  canChangePrice?: boolean;
  markupRange?: Array<number>;
  priceRange?: Array<number>;
  productAvailability: boolean;
  productAvailabilityMessage: string;
  productAvailabilityLink: string;
  productTermsAndConditionLink: string;
  productMaxMarkup: number;
  showEstimatedDollarAmount: boolean;
  productUnit: string;
  productUnitText: string;
  productSubTitle: string;
  save: (entityId: string, entityType: string) => {};
  updating: boolean;
  dirty: boolean;
  productOptIn?: boolean;
}

export interface IProductResponse {
  productTitle: string;
  meta: {
    description: string;
    canChangePrice: boolean;
    markupRange: number[];
    priceRange: number[];
    helpArticle: string;
    productAvailabilityMessage: string;
    productAvailabilityLink: string;
    productTermsAndConditionLink: string;
    productMaxMarkup: number;
    showEstimatedDollarAmount: boolean;
    productUnit: number;
    productUnitText: string;
    productSubTitle: string;
  };
  config: {
    enabled: boolean;
    markup: number;
    basePrice: number;
    optIn: boolean;
  }
  product: string;
  productAvailability? : boolean;
}

export default class ProductRebilling {
  constructor(payload: IProductResponse) {
    this.title = payload.productTitle
    this.description = payload.meta?.description || '';
    this.type = payload.product;
    this.rebillingEnabled = payload.config.enabled;
    this.helpArticle = payload.meta?.helpArticle || '';
    this.markup = payload.config.markup ?? 5;
    this.price = payload.config.basePrice;
    this.canChangePrice = payload.meta?.canChangePrice || false;
    this.markupRange = payload.meta?.markupRange || [5, 1000];
    this.priceRange = payload.meta?.priceRange || [];
    this.productAvailability = payload.productAvailability || false;
    this.productAvailabilityMessage = payload.meta?.productAvailabilityMessage || 'Coming soon';
    this.productAvailabilityLink = payload.meta?.productAvailabilityLink || '';
    this.productTermsAndConditionLink = payload.meta?.productTermsAndConditionLink || '';
    this.productMaxMarkup = payload.meta?.productMaxMarkup || 10;
    this.showEstimatedDollarAmount = payload.meta?.showEstimatedDollarAmount ?? true;
    this.productUnit = payload.meta?.productUnit || 1;
    this.productUnitText = payload.meta?.productUnitText || payload.product;
    this.productSubTitle = payload.meta?.productSubTitle || `These are margins that are billed to your clients over the ${payload.productTitle} cost
    borne by you`;
    this._updating = false;
    this._dirty = false;
    this.productOptIn = payload.config?.optIn;
  }

  private _title = '';
  private _description = '';
  private _type = '';
  private _rebillingEnabled = false;
  private _helpArticle = '';
  private _markup = 5;
  private _price = 1;
  private _canChangePrice = false;
  private _markupRange = [5, 1000];
  private _priceRange = [1, 100];
  private _productAvailability = true;
  private _updating = false;
  private _dirty = false;
  private _productAvailabilityMessage = '';
  private _productAvailabilityLink = '';
  private _productTermsAndConditionLink = '';
  private _productMaxMarkup = 10;
  private _showEstimatedDollarAmount = true;
  private _productUnit = 1;
  private _productUnitText = '';
  private _productSubTitle = '';
  private _productOptIn = false;


  get title(): string {
    return this._title;
  }

  set title(value: string) {
    this._title = value;
  }

  get description(): string {
    return this._description;
  }

  set description(value: string) {
    this._description = value;
  }

  get type(): string {
    return this._type;
  }

  set type(value: string) {
    this._type = value;
  }

  get rebillingEnabled(): boolean {
    return this._rebillingEnabled;
  }

  set rebillingEnabled(value: boolean) {
    this._rebillingEnabled = value;
  }

  get markup(): number {
    return this._markup;
  }

  set markup(value: number) {
    this._markup = value;
  }

  get price(): number {
    return this._price;
  }

  set price(value: number) {
    this._price = value;
  }

  get canChangePrice(): boolean {
    return this._canChangePrice;
  }

  set canChangePrice(value: boolean) {
    this._canChangePrice = value;
  }

  get helpArticle(): string {
    return this._helpArticle;
  }

  set helpArticle(value: string) {
    this._helpArticle = value;
  }

  get markupRange(): Array<number> {
    return this._markupRange;
  }

  set markupRange(value: Array<number>) {
    this._markupRange = value;
  }

  get priceRange(): Array<number> {
    return this._priceRange;
  }

  set priceRange(value: Array<number>) {
    this._priceRange = value;
  }

  get productAvailability(): boolean {
    return this._productAvailability;
  }

  set productAvailability(value: boolean) {
    this._productAvailability = value;
  }

  get productAvailabilityMessage(): string {
    return this._productAvailabilityMessage;
  }

  set productAvailabilityMessage(value: string) {
    this._productAvailabilityMessage = value;
  }

  get productAvailabilityLink(): string {
    return this._productAvailabilityLink;
  }

  set productAvailabilityLink(value: string) {
    this._productAvailabilityLink = value;
  }

  get productTermsAndConditionLink(): string {
    return this._productTermsAndConditionLink;
  }

  set productTermsAndConditionLink(value: string) {
    this._productTermsAndConditionLink = value;
  }

  get productMaxMarkup(): number {
    return this._productMaxMarkup;
  }

  set productMaxMarkup(value: number) {
    this._productMaxMarkup = value;
  }

  get showEstimatedDollarAmount(): boolean {
    return this._showEstimatedDollarAmount;
  }

  set showEstimatedDollarAmount(value: boolean) {
    this._showEstimatedDollarAmount = value;
  }

  get productUnit(): number {
    return this._productUnit;
  }

  set productUnit(value: number) {
    this._productUnit = value;
  }

  get productUnitText(): string {
    return this._productUnitText;
  }

  set productUnitText(value: string) {
    this._productUnitText = value;
  }

  get productSubTitle(): string {
    return this._productSubTitle;
  }

  set productSubTitle(value: string) {
    this._productSubTitle = value;
  }

  get updating(): boolean {
    return this._updating;
  }

  get dirty(): boolean {
    return this._dirty;
  }

  public markAsDirty() {
    this._dirty = true;
  }

  public markAsClean() {
    this._dirty = false;
  }

  public async save(entityId: string, entityType: string, companyId: string = '') {
    this._updating = true;
    try {

        await ProductRebillingService.update(entityType as string, entityId as string, this as IProductRebilling, companyId as string);

      if (this.dirty) {
        this._dirty = false;
      }
    } catch (error) {
      throw error;
    } finally {
      this._updating = false;
    }
  }
  public async saveRebilling(companyId: string) {
    this._updating = true;
    try {

        await ProductRebillingService.updateRebilling(companyId as string, this as IProductRebilling);

      if (this.dirty) {
        this._dirty = false;
      }
    } catch (error) {
      throw error;
    } finally {
      this._updating = false;
    }
  }

  get productOptIn(): boolean {
    return this._productOptIn;
  }

  set productOptIn(value: boolean) {
    this._productOptIn = value;
  }

}
